import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDarkMode } from '../../App';

const photographyProjects = [
  {
    title: 'The Bridle Path Estate',
    description:
      "A commanding Bridle Path residence captured with precision and purpose. Sweeping interiors, curated finishes, and dramatic natural light define this ultra-premium property. Our photography emphasized the home's architectural scale, refined materiality, and seamless indoor-outdoor flow — positioning it as one of Toronto's most distinguished listings.",
    images: [
      'https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/217c7667e6c4b179fdc1f9d1818715a1.jpeg',
      'https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/792f65b805acb2e5c0b8745d8f4d8432.jpeg',
      'https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/84bd5841c96be86651b156f68a674328.jpeg',
      'https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/511d78c8d18ff4ac269f939a248da1d0.jpeg',
    ],
  },
  {
    title: 'Rosedale Manor',
    description:
      "A stately Rosedale manor where timeless architecture meets thoughtful modern restoration. Original millwork, soaring ceilings, and curated interiors speak to a legacy of refined living. Our photography honoured the home's heritage details while showcasing its contemporary comfort — a seamless dialogue between past and present.",
    images: [
      'https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/2ebafa4690b8c256e1065b71991d307d.jpeg',
      'https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/db056135e4e8a80436f475f05e153fd7.jpeg',
      'https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/a13da90ec184dbc9ba852a5a48c22d44.jpeg',
      'https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/a50eb996d21aead5fb1cfbd431bef917.jpeg',
    ],
  },
  {
    title: 'Yorkville Penthouse',
    description:
      "Perched above Toronto's most prestigious address, this Yorkville penthouse commands attention through bold design and panoramic city views. Floor-to-ceiling glazing, sculptural interiors, and curated art create a living experience that feels both intimate and expansive. Our photography captured the interplay of light, height, and refined luxury.",
    images: [
      'https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/6d7d99d945296f91483bf26528db687e.jpeg',
      'https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/13e64bb4cc530ec96e8fa1db277d8435.jpeg',
      'https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/6ed35affbbd677ab269c793300409c00.jpeg',
      'https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/73431f1ecf10ef30590bd33e0340904e.jpeg',
    ],
  },
];

export default function Portfolio() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  // Safely handle IntersectionObserver support
  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      // Fallback: simply show all elements
      document.querySelectorAll('.scroll-reveal').forEach((el) => {
        el.classList.add('animate-visible');
      });
      return;
    }

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.scroll-reveal');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={`min-h-screen ${
        isDarkMode ? 'bg-[#1a1a1a] text-white' : 'bg-[#faf9f7] text-[#2a2a2a]'
      } transition-colors duration-500`}
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <style>{`
        .scroll-reveal {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .scroll-reveal.animate-visible {
          opacity: 1;
          transform: translateY(0);
        }
        .scroll-reveal-delay-1 { transition-delay: 0.1s; }
        .scroll-reveal-delay-2 { transition-delay: 0.2s; }
        .scroll-reveal-delay-3 { transition-delay: 0.3s; }
        .video-wrapper {
          transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .video-wrapper:hover {
          transform: scale(1.015);
        }
        .photo-img {
          transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .photo-img:hover {
          transform: scale(1.03);
        }
      `}</style>

      {/* Top Right Icons */}
      <div className="fixed top-4 right-4 md:top-8 md:right-8 flex items-center gap-3 md:gap-4 z-50">
        <button
          onClick={toggleDarkMode}
          className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center ${
            isDarkMode
              ? 'text:white/70 hover:text:white'
              : 'text:[#2a2a2a]/70 hover:text:[#2a2a2a]'
          } transition-colors cursor-pointer`}
        >
          <i className={`ri-${isDarkMode ? 'moon' : 'sun'}-line text-lg md:text-xl`}></i>
        </button>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center ${
            isDarkMode
              ? 'text:white/70 hover:text:white'
              : 'text:[#2a2a2a]/70 hover:text:[#2a2a2a]'
          } transition-colors cursor-pointer`}
        >
          <i className="ri-menu-line text-lg md:text-xl"></i>
        </button>
      </div>

      {/* Slide-out Menu */}
      <div
        className={`fixed top-0 right-0 h-full w-72 md:w-80 ${
          isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#f5f4f2]'
        } shadow-2xl transform transition-transform duration-500 z-40 ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="p-6 md:p-8">
          <button
            onClick={() => setMenuOpen(false)}
            className={`absolute top-6 right-6 md:top-8 md:right-8 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center ${
              isDarkMode
                ? 'text:white/70 hover:text:white'
                : 'text:[#2a2a2a]/70 hover:text:[#2a2a2a]'
            } transition-colors cursor-pointer`}
          >
            <i className="ri-close-line text-lg md:text-xl"></i>
          </button>
          <nav className="mt-16 md:mt-20 space-y-5 md:space-y-6">
            <Link
              to="/home"
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${
                isDarkMode
                  ? 'text:white/90 hover:text:white'
                  : 'text-[#2a2a2a]/90 hover:text:[#2a2a2a]'
              } transition-colors`}
            >
              Home
            </Link>
            <a
              href="https://order.cinestate.ca/booking/new"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${
                isDarkMode
                  ? 'text:white/90 hover:text:white'
                  : 'text-[#2a2a2a]/90 hover:text:[#2a2a2a]'
              } transition-colors`}
            >
              Listing Packages
            </a>
            <Link
              to="/packages"
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${
                isDarkMode
                  ? 'text:white/90 hover:text:white'
                  : 'text-[#2a2a2a]/90 hover:text:[#2a2a2a]'
              } transition-colors`}
            >
              Content Packages
            </Link>
            <Link
              to="/staging"
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${
                isDarkMode
                  ? 'text:white/90 hover:text:white'
                  : 'text-[#2a2a2a]/90 hover:text:[#2a2a2a]'
              } transition-colors`}
            >
              Home Staging
            </Link>
            {/* Portfolio Dropdown */}
            <div>
              <button
                onClick={() => setPortfolioOpen(!portfolioOpen)}
                className={`flex items-center justify-between w-full text-sm md:text-base font-normal tracking-[0.15em] uppercase ${
                  isDarkMode ? 'text-white/90 hover:text-white' : 'text-[#2a2a2a]/90 hover:text-[#2a2a2a]'
                } transition-colors cursor-pointer`}
              >
                <span>Portfolio</span>
                <i className={`${portfolioOpen ? 'ri-subtract-line' : 'ri-add-line'} text-xs`}></i>
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${portfolioOpen ? 'max-h-24 mt-3' : 'max-h-0'}`}>
                <div className="ml-4 space-y-3">
                  <Link
                    to="/portfolio"
                    onClick={() => setMenuOpen(false)}
                    className={`block text-xs md:text-sm font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/55 hover:text-white' : 'text-[#2a2a2a]/55 hover:text-[#2a2a2a]'} transition-colors`}
                  >
                    Residential
                  </Link>
                  <Link
                    to="/portfolio/commercial"
                    onClick={() => setMenuOpen(false)}
                    className={`block text-xs md:text-sm font-normal tracking-[0.15em] uppercase ${isDarkMode ? 'text-white/55 hover:text-white' : 'text-[#2a2a2a]/55 hover:text-[#2a2a2a]'} transition-colors`}
                  >
                    Commercial
                  </Link>
                </div>
              </div>
            </div>
            <Link
              to="/home#contact"
              onClick={() => setMenuOpen(false)}
              className={`block text-sm md:text-base font-normal tracking-[0.15em] uppercase ${
                isDarkMode
                  ? 'text:white/70 hover:text:white'
                  : 'text-[#2a2a2a]/70 hover:text:[#2a2a2a]'
              } transition-colors`}
            >
              Contact
            </Link>
          </nav>
          <div className="absolute bottom-8 md:bottom-12 left-6 md:left-8 flex gap-3 md:gap-4">
            <a
              href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${
                isDarkMode
                  ? 'border:white/20 text:white/70 hover:text:white'
                  : 'border-[#2a2a2a]/20 text-[#2a2a2a]/70 hover:border-[#2a2a2a] hover:text-[#2a2a2a]'
              } transition-colors`}
            >
              <i className="ri-instagram-line text-sm md:text-base"></i>
            </a>
            <a
              href="https://youtube.com/@kianikanstudio3017?si=QvudTCaWtHR5bmy8"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${
                isDarkMode
                  ? 'border:white/20 text:white/70 hover:text:white'
                  : 'border-[#2a2a2a]/20 text-[#2a2a2a]/70 hover:border-[#2a2a2a] hover:text-[#2a2a2a]'
              } transition-colors`}
            >
              <i className="ri-youtube-line text-sm md:text-base"></i>
            </a>
            <a
              href="https://www.tiktok.com/@kianikanstudio.ca?_r=1&_t=ZS-91yzSCjzfEY"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${
                isDarkMode
                  ? 'border:white/20 text:white/70 hover:text:white'
                  : 'border-[#2a2a2a]/20 text-[#2a2a2a]/70 hover:border-[#2a2a2a] hover:text-[#2a2a2a]'
              } transition-colors`}
            >
              <i className="ri-tiktok-line text-sm md:text-base"></i>
            </a>
            <a
              href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border ${
                isDarkMode
                  ? 'border:white/20 text:white/70 hover:text:white'
                  : 'border-[#2a2a2a]/20 text-[#2a2a2a]/70 hover:border-[#2a2a2a] hover:text-[#2a2a2a]'
              } transition-colors`}
            >
              <i className="ri-facebook-fill text-sm md:text-base"></i>
            </a>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="fixed inset-0 bg-black/50 z-30" onClick={() => setMenuOpen(false)}></div>
      )}

      {/* Logo */}
      <div className="fixed top-4 left-4 md:top-8 md:left-8 z-40">
        <Link to="/" className="block">
          <img
            src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/91365b59b7e8e66ad4ab2980732fafc0.png"
            alt="CINÉSTATE"
            className={`h-5 md:h-7 w-auto ${isDarkMode ? 'brightness-0 invert' : ''}`}
          />
        </Link>
      </div>

      {/* Hero Section */}
      <section className="min-h-[65vh] flex flex-col justify-center px-4 sm:px-6 md:px-8 lg:px-16 pt-24 pb-12">
        <div className="max-w-5xl mx-auto w-full">
          <p
            className={`scroll-reveal text-[10px] sm:text-xs tracking-[0.25em] uppercase mb-6 md:mb-8 ${
              isDarkMode ? 'text-[#d4c5b0]' : 'text-[#8a7d6d]'
            }`}
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Portfolio
          </p>
          <h1
            className="scroll-reveal scroll-reveal-delay-1 text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-normal mb-8 md:mb-12 leading-[1.1] tracking-wide"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Our Work
          </h1>
          <div className="scroll-reveal scroll-reveal-delay-2 max-w-xl">
            <p
              className={`text-base sm:text-lg md:text-xl leading-relaxed font-light ${
                isDarkMode ? 'text:white/60' : 'text-[#2a2a2a]/60'
              }`}
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              A curated collection of cinematic video formats and premium photography — each crafted to serve a
              distinct strategic purpose in luxury real estate marketing.
            </p>
            <div className={`w-20 h-px mt-10 md:mt-12 ${isDarkMode ? 'bg-[#d4c5b0]/30' : 'bg-[#8a7d6d]/30'}`}></div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* SIGNATURE CINEMATIC SECTION */}
      {/* ═══════════════════════════════════════════ */}
      <section
        className={`py-16 sm:py-20 md:py-28 lg:py-32 px-4 sm:px-6 md:px-8 lg:px-16 ${
          isDarkMode ? 'bg-[#141414]' : 'bg-[#f5f3f0]'
        }`}
      >
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="scroll-reveal mb-14 md:mb-20">
            <p
              className={`text-[10px] sm:text-xs tracking-[0.25em] uppercase mb-4 ${
                isDarkMode ? 'text-[#d4c5b0]' : 'text-[#8a7d6d]'
              }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Video Format 01
            </p>
            <h2
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-normal mb-5 md:mb-7 leading-[1.1] tracking-wide"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Signature Cinematic
            </h2>
            <div className={`w-16 h-px ${isDarkMode ? 'bg-white/15' : 'bg-[#2a2a2a]/15'}`}></div>
          </div>

          {/* Two Videos Side by Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 lg:gap-20">
            {/* Video 1 */}
            <div className="scroll-reveal">
              <div className="mb-6 md:mb-8">
                <h3
                  className={`text-lg sm:text-xl md:text-2xl font-normal mb-3 leading-tight tracking-wide ${
                    isDarkMode ? 'text:white' : 'text-[#2a2a2a]'
                  }`}
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Elegant Property Study
                </h3>
                <p
                  className={`text-sm sm:text-base leading-relaxed font-light ${
                    isDarkMode ? 'text:white/55' : 'text-[#2a2a2a]/55'
                  }`}
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Our elegant, timeless interior-focused property study — crafted for design-driven homes.
                </p>
              </div>
              <div
                className={`video-wrapper relative rounded-lg overflow-hidden ${
                  isDarkMode
                    ? 'shadow-[0_12px_40px_rgba(0,0,0,0.3)]'
                    : 'shadow-[0_12px_40px_rgba(0,0,0,0.08)]'
                }`}
                style={{ aspectRatio: '9/16', maxWidth: '400px' }}
              >
                <iframe
                  src="https://www.instagram.com/reel/DT5jUqIjpNj/embed"
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  scrolling="no"
                  allowTransparency="true"
                  allow="encrypted-media"
                  title="Elegant Property Study"
                ></iframe>
              </div>
            </div>

            {/* Video 2 */}
            <div className="scroll-reveal scroll-reveal-delay-2">
              <div className="mb-6 md:mb-8">
                <h3
                  className={`text-lg sm:text-xl md:text-2xl font-normal mb-3 leading-tight tracking-wide ${
                    isDarkMode ? 'text:white' : 'text-[#2a2a2a]'
                  }`}
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Narrative Storytelling
                </h3>
                <p
                  className={`text-sm sm:text-base leading-relaxed font-light ${
                    isDarkMode ? 'text:white/55' : 'text-[#2a2a2a]/55'
                  }`}
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Our narrative storytelling format where we handle the script and property analysis.
                </p>
              </div>
              <div
                className={`video-wrapper relative rounded-lg overflow-hidden ${
                  isDarkMode
                    ? 'shadow-[0_12px_40px_rgba(0,0,0,0.3)]'
                    : 'shadow-[0_12px_40px_rgba(0,0,0,0.08)]'
                }`}
                style={{ aspectRatio: '9/16', maxWidth: '400px' }}
              >
                <iframe
                  src="https://www.instagram.com/reel/DULtnwrDovT/embed"
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  scrolling="no"
                  allowTransparency="true"
                  allow="encrypted-media"
                  title="Narrative Storytelling"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className={`w-full h-px ${isDarkMode ? 'bg:white/5' : 'bg-[#2a2a2a]/5'}`}></div>

      {/* ═══════════════════════════════════════════ */}
      {/* SIGNATURE DYNAMIC SECTION */}
      {/* ═══════════════════════════════════════════ */}
      <section
        className={`py-16 sm:py-20 md:py-28 lg:py-32 px-4 sm:px-6 md:px-8 lg:px-16 ${
          isDarkMode ? 'bg-[#1a1a1a]' : 'bg-[#faf9f7]'
        }`}
      >
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="scroll-reveal mb-14 md:mb-20">
            <p
              className={`text-[10px] sm:text-xs tracking-[0.25em] uppercase mb-4 ${
                isDarkMode ? 'text-[#d4c5b0]' : 'text-[#8a7d6d]'
              }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Video Format 02
            </p>
            <h2
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-normal mb-5 md:mb-7 leading-[1.1] tracking-wide"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Signature Dynamic
            </h2>
            <div className={`w-16 h-px ${isDarkMode ? 'bg:white/15' : 'bg-[#2a2a2a]/15'}`}></div>
          </div>

          {/* Two Videos Side by Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 lg:gap-20">
            {/* Video 1 */}
            <div className="scroll-reveal">
              <div className="mb-6 md:mb-8">
                <h3
                  className={`text-lg sm:text-xl md:text-2xl font-normal mb-3 leading-tight tracking-wide ${
                    isDarkMode ? 'text:white' : 'text-[#2a2a2a]'
                  }`}
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  High-Energy Launch Edit
                </h3>
                <p
                  className={`text-sm sm:text-base leading-relaxed font-light ${
                    isDarkMode ? 'text:white/55' : 'text-[#2a2a2a]/55'
                  }`}
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Dynamic, rhythm-driven edits designed for maximum social impact and competitive market attention.
                </p>
              </div>
              <div
                className={`video-wrapper relative rounded-lg overflow-hidden ${
                  isDarkMode
                    ? 'shadow-[0_12px_40px_rgba(0,0,0,0.3)]'
                    : 'shadow-[0_12px_40px_rgba(0,0,0,0.08)]'
                }`}
                style={{ aspectRatio: '9/16', maxWidth: '400px' }}
              >
                <iframe
                  src="https://www.instagram.com/reel/DRbFA1OjnAe/embed"
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  scrolling="no"
                  allowTransparency="true"
                  allow="encrypted-media"
                  title="High-Energy Launch Edit"
                ></iframe>
              </div>
            </div>

            {/* Video 2 */}
            <div className="scroll-reveal scroll-reveal-delay-2">
              <div className="mb-6 md:mb-8">
                <h3
                  className={`text-lg sm:text-xl md:text-2xl font-normal mb-3 leading-tight tracking-wide ${
                    isDarkMode ? 'text:white' : 'text-[#2a2a2a]'
                  }`}
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Agent On Camera
                </h3>
                <p
                  className={`text-sm sm:text-base leading-relaxed font-light ${
                    isDarkMode ? 'text:white/55' : 'text-[#2a2a2a]/55'
                  }`}
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Authentic, personality-forward content that builds agent authority and polished social presence.
                </p>
              </div>
              <div
                className={`video-wrapper relative rounded-lg overflow-hidden ${
                  isDarkMode
                    ? 'shadow-[0_12px_40px_rgba(0,0,0,0.3)]'
                    : 'shadow-[0_12px_40px_rgba(0,0,0,0.08)]'
                }`}
                style={{ aspectRatio: '9/16', maxWidth: '400px' }}
              >
                <iframe
                  src="https://www.instagram.com/reel/DVEW-DXDkl0/embed"
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  scrolling="no"
                  allowTransparency="true"
                  allow="encrypted-media"
                  title="Agent On Camera"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className={`w-full h-px ${isDarkMode ? 'bg:white/5' : 'bg-[#2a2a2a]/5'}`}></div>

      {/* ═══════════════════════════════════════════ */}
      {/* PREMIUM PHOTOGRAPHY SECTION */}
      {/* ═══════════════════════════════════════════ */}
      <section
        className={`py-16 sm:py-20 md:py-28 lg:py-32 px-4 sm:px-6 md:px-8 lg:px-16 ${
          isDarkMode ? 'bg-[#141414]' : 'bg-[#f5f3f0]'
        }`}
      >
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="scroll-reveal mb-14 md:mb-20">
            <p
              className={`text-[10px] sm:text-xs tracking-[0.25em] uppercase mb-4 ${
                isDarkMode ? 'text-[#d4c5b0]' : 'text-[#8a7d6d]'
              }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Photography
            </p>
            <h2
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-normal mb-5 md:mb-7 leading-[1.1] tracking-wide"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Premium Photography
            </h2>
            <p
              className={`text-sm sm:text-base md:text-lg leading-relaxed font-light max-w-2xl ${
                isDarkMode ? 'text:white/55' : 'text-[#2a2a2a]/55'
              }`}
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Our photography elevates real estate and architecture by capturing their essence in every shot.
              Refined composition, expert lighting, and a deep understanding of space.
            </p>
            <div className={`w-16 h-px mt-8 ${isDarkMode ? 'bg:white/15' : 'bg-[#2a2a2a]/15'}`}></div>
          </div>

          {/* Photography Projects */}
          <div className="space-y-20 md:space-y-28 lg:space-y-36">
            {photographyProjects.map((project, index) => (
              <div key={project.title} className="scroll-reveal">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 lg:gap-16">
                  {/* Text Content */}
                  <div className={`lg:col-span-4 flex flex-col justify-center ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <h3
                      className={`text-xl sm:text-2xl md:text-3xl font-normal mb-4 md:mb-6 leading-tight tracking-wide ${
                        isDarkMode ? 'text:white' : 'text-[#2a2a2a]'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {project.title}
                    </h3>
                    <p
                      className={`text-sm sm:text-base leading-relaxed font-light mb-6 md:mb-8 ${
                        isDarkMode ? 'text:white/55' : 'text-[#2a2a2a]/55'
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {project.description}
                    </p>
                    <Link
                      to="/home#contact"
                      className={`inline-flex items-center gap-2 text-xs tracking-widest uppercase hover:gap-3 transition-all font-normal cursor-pointer ${
                        isDarkMode ? 'text:[#d4c5b0] hover:text:white' : 'text:[#8a7d6d] hover:text:[#2a2a2a]'
                      }`}
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      <span className="whitespace-nowrap">Enquire Now</span>
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>

                  {/* Photo Grid */}
                  <div className={`lg:col-span-8 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div className="grid grid-cols-2 gap-3 md:gap-4">
                      {/* Main Image */}
                      <div className="col-span-2 overflow-hidden rounded-lg">
                        <div className="w-full h-[250px] sm:h-[300px] md:h-[380px] lg:h-[420px]">
                          <img
                            src={project.images[0]}
                            alt={`${project.title} - Main`}
                            className="photo-img w-full h-full object-cover object-top cursor-pointer"
                          />
                        </div>
                      </div>
                      {/* Smaller Images */}
                      {project.images.slice(1).map((img, imgIndex) => (
                        <div
                          key={imgIndex}
                          className={`overflow-hidden rounded-lg ${
                            imgIndex === 2 ? 'col-span-2 sm:col-span-1' : ''
                          }`}
                        >
                          <div className="w-full h-[160px] sm:h-[180px] md:h-[220px] lg:h-[260px]">
                            <img
                              src={img}
                              alt={`${project.title} - ${imgIndex + 2}`}
                              className="photo-img w-full h-full object-cover object-top cursor-pointer"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Divider between projects */}
                {index < photographyProjects.length - 1 && (
                  <div className={`w-full h-px mt-20 md:mt-28 lg:mt-36 ${isDarkMode ? 'bg:white/8' : 'bg-[#2a2a2a]/8'}`}></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className={`py-20 md:py-28 lg:py-36 px-4 sm:px-6 md:px-8 lg:px-16 ${
          isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#ebe8e4]'
        }`}
      >
        <div className="max-w-3xl mx-auto text-center">
          <h2
            className="scroll-reveal text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-normal mb-6 md:mb-8 leading-[1.15] tracking-wide"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Ready to Elevate Your Listings?
          </h2>
          <p
            className={`scroll-reveal scroll-reveal-delay-1 text-sm sm:text-base md:text-lg leading-relaxed font-light mb-10 md:mb-14 max-w-xl mx-auto ${
              isDarkMode ? 'text:white/60' : 'text-[#2a2a2a]/60'
            }`}
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Let us craft cinematic content that positions your properties — and your brand — at the forefront of the
            market.
          </p>
          <div className="scroll-reveal scroll-reveal-delay-2 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/home#contact"
              className={`inline-block px-8 py-3.5 md:px-10 md:py-4 ${
                isDarkMode ? 'bg:white text:[#0d0d0d] hover:bg:white/90' : 'bg:[#2a2a2a] text:white hover:bg:[#2a2a2a]/90'
              } text-xs md:text-sm tracking-widest uppercase transition-colors font-normal whitespace-nowrap cursor-pointer`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Get In Touch
            </Link>
            <a
              href="https://order.cinestate.ca/booking/new"
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-block px-8 py-3.5 md:px-10 md:py-4 border ${
                isDarkMode ? 'border:white/30 text:white hover:border:white' : 'border:[#2a2a2a]/30 text:[#2a2a2a] hover:border:[#2a2a2a]'
              } text-xs md:text-sm tracking-widest uppercase transition-colors font-normal whitespace-nowrap cursor-pointer`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Book Now
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className={`border-t ${isDarkMode ? 'border:white/10' : 'border-[#2a2a2a]/10'} ${
          isDarkMode ? 'bg-[#1a1a1a]' : 'bg-[#faf9f7]'
        }`}
      >
        <div className="px-4 sm:px-6 md:px-8 lg:px-16 py-10 sm:py-12 md:py-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col gap-6 sm:gap-8 md:gap-12">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
                <Link to="/" className="block">
                  <img
                    src="https://static.readdy.ai/image/530c8f921e7d182176cffe22debeaa7c/91365b59b7e8e66ad4ab2980732fafc0.png"
                    alt="CINÉSTATE"
                    className={`h-4 sm:h-5 md:h-7 w-auto ${isDarkMode ? 'brightness-0 invert' : ''}`}
                  />
                </Link>
                <div className="flex gap-2 sm:gap-3 md:gap-4">
                  <a
                    href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${
                      isDarkMode ? 'text:white/70 hover:text:white' : 'text:[#2a2a2a]/70 hover:text:[#2a2a2a]'
                    } transition-colors cursor-pointer`}
                  >
                    <i className="ri-instagram-line text-sm md:text-base"></i>
                  </a>
                  <a
                    href="https://youtube.com/@kianikanstudio3017?si=QvudTCaWtHR5bmy8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${
                      isDarkMode ? 'text:white/70 hover:text:white' : 'text:[#2a2a2a]/70 hover:text:[#2a2a2a]'
                    } transition-colors cursor-pointer`}
                  >
                    <i className="ri-youtube-line text-sm md:text-base"></i>
                  </a>
                  <a
                    href="https://www.tiktok.com/@kianikanstudio.ca?_r=1&_t=ZS-91yzSCjzfEY"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${
                      isDarkMode ? 'text:white/70 hover:text:white' : 'text:[#2a2a2a]/70 hover:text:[#2a2a2a]'
                    } transition-colors cursor-pointer`}
                  >
                    <i className="ri-tiktok-line text-sm md:text-base"></i>
                  </a>
                  <a
                    href="https://www.instagram.com/kianikanstudio?igsh=M3RoZ25wc25oZHY0"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center ${
                      isDarkMode ? 'text:white/70 hover:text:white' : 'text:[#2a2a2a]/70 hover:text:[#2a2a2a]'
                    } transition-colors cursor-pointer`}
                  >
                    <i className="ri-facebook-fill text-sm md:text-base"></i>
                  </a>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 sm:gap-5 md:gap-8">
                <a
                  href="https://order.cinestate.ca/booking/new"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-[10px] sm:text-xs md:text-sm tracking-widest uppercase ${
                    isDarkMode ? 'text:white/70 hover:text:white' : 'text:[#2a2a2a]/70 hover:text:[#2a2a2a]'
                  } transition-colors font-normal`}
                >
                  Listing Packages
                </a>
                <Link
                  to="/packages"
                  className={`text-[10px] sm:text-xs md:text-sm tracking-widest uppercase ${
                    isDarkMode ? 'text:white/70 hover:text:white' : 'text:[#2a2a2a]/70 hover:text:[#2a2a2a]'
                  } transition-colors font-normal`}
                >
                  Content Packages
                </Link>
                <Link
                  to="/staging"
                  className={`text-[10px] sm:text-xs md:text-sm tracking-widest uppercase ${
                    isDarkMode ? 'text:white/70 hover:text:white' : 'text:[#2a2a2a]/70 hover:text:[#2a2a2a]'
                  } transition-colors font-normal`}
                >
                  Home Staging
                </Link>
                <Link
                  to="/portfolio"
                  className={`text-[10px] sm:text-xs md:text-sm tracking-widest uppercase ${
                    isDarkMode ? 'text:white/70 hover:text:white' : 'text:[#2a2a2a]/70 hover:text:[#2a2a2a]'
                  } transition-colors font-normal`}
                >
                  Portfolio
                </Link>
                <Link
                  to="/home#contact"
                  className={`text-[10px] sm:text-xs md:text-sm tracking-widest uppercase ${
                    isDarkMode ? 'text:white/70 hover:text:white' : 'text:[#2a2a2a]/70 hover:text:[#2a2a2a]'
                  } transition-colors font-normal`}
                >
                  Contact
                </Link>
              </div>
            </div>
            <div className={`w-full h-px ${isDarkMode ? 'bg:white/10' : 'bg-[#2a2a2a]/10'} mb-4 sm:mb-6 md:mb-8`}></div>
            <div className={`flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-3 md:gap-4 text-[10px] sm:text-xs md:text-sm ${
              isDarkMode ? 'text:white/50' : 'text:[#2a2a2a]/50'
            }`}>
              <p className="font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                © 2025 Cinéstate. All rights reserved.
              </p>
              <a
                href="https://readdy.ai/?ref=logo"
                className={`${isDarkMode ? 'hover:text:white' : 'hover:text:#2a2a2a'} transition-colors font-light`}
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
